import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, CreditCard, BookOpen, Clock } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, BACKEND_URL } from "@/lib/api";
import EbookPaymentModal from "@/components/site/EbookPaymentModal";

export default function BookCard ({ book }) {
    const { add, items } = useCart();
    const { t, lang } = useI18n();
    const { user } = useAuth();
    const nav = useNavigate();
    const [showPayment, setShowPayment] = useState(false);
    const [ebookStatus, setEbookStatus] = useState(null);

    useEffect(() => {
        if (!isEbook || !user) return;
        api.get("/profile/ebook-subscriptions")
            .then((r) => {
                const subs = r.data || [];
                const sub = subs.find((s) => s.book_id === book.id);
                if (sub) setEbookStatus(sub.status);
            })
            .catch(() => {});
    }, [book, user]);

    const alreadyInCart = items?.some(
        (item) => item.book_id === book.id || item.id === book.id
    );

    const isEbook = book.category === "ebook";
    const price = book.discount_price ?? book.price;
    const hasDiscount =
        !isEbook && book.discount_price && book.discount_price < book.price;

    const fullUrl = (u) =>
        u?.startsWith("/") ? `${ BACKEND_URL }${ u }` : u;

    return (
        <div
            className="book-card relative bg-white rounded-2xl overflow-hidden border border-brand-light/60 shadow-soft flex flex-col"
            data-testid="book-card"
        >
            {/* IMAGE SECTION */}
            <Link to={`/book/${ book.id }`} className="block">
                <div className="aspect-[3/4] relative w-full bg-gradient-to-br from-[#DCE7F1] to-[#AFC4D6]/40 overflow-hidden">
                    {book.cover_image ? (
                        <img
                            src={fullUrl(book.cover_image)}
                            alt={book.title}
                            loading="lazy"
                            className="max-w-full max-h-full h-full w-full object-cover rounded-[8px] p-1 lg:p-2"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-brand-navy/50">
                            No Cover
                        </div>
                    )}
                    {/* DISCOUNT BADGE */}
                    {!isEbook && hasDiscount && book.stock > 0 && (
                        <div className="absolute top-3 left-3 bg-brand-red text-white text-[10px] font-bold px-2 py-1 rounded-md">
                            {Math.round(
                                (1 - book.discount_price / book.price) *
                                100
                            )}
                            % OFF
                        </div>
                    )}
                    {/* OUT OF STOCK OVERLAY */}
                    {book.stock <= 0 && (
                        <div className="absolute top-3 right-3 bg-brand-red text-white text-[10px] font-bold px-2 py-1 rounded-md">
                            Out of Stock
                        </div>
                    )}
                    {/* STOCK STATUS */}
                    {book.stock > 0 && (
                        <div className="absolute top-3 right-3 bg-brand-red text-white text-[10px] font-bold px-2 py-1 rounded-md">
                            In Stock
                        </div>
                    )}
                </div>
            </Link>
            {/* CONTENT */}
            <div className="p-2 md:p-4 flex flex-col flex-1">
                <Link to={`/book/${ book.id }`} className="block flex-1">
                    <h3 className="text-sm md:text-base font-semibold text-brand-navy text-center md:text-start">
                        {lang === "en" && book.title_en
                            ? book.title_en
                            : book.title}
                    </h3>
                </Link>
                {/* PRICE + BUTTON */}
                <div className="mt-2 md:mt-4 flex flex-col md:flex-row items-center md:items-end justify-between">
                    {!isEbook && (
                        <div>
                            <span className="text-sm md:text-lg font-bold text-brand-royal">
                                ৳{price}
                            </span>
                            {hasDiscount && (
                                <span className="ml-2 text-xs text-brand-navy/40 line-through">
                                    ৳{book.price}
                                </span>
                            )}
                        </div>
                    )}
                    {isEbook && book.stock <= 0 ? (
                        <div className="bg-brand-light/40 text-brand-navy/50 text-xs font-semibold px-3 py-2 rounded-lg mt-1.5 md:mt-0">
                            Out of Stock
                        </div>
                    ) : isEbook ? (
                        ebookStatus === "active" ? (
                            <Link
                                to={`/profile`}
                                className="btn-primary text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 mt-1.5 md:mt-0"
                            >
                                <BookOpen className="w-3.5 h-3.5" />
                                বই পড়ুন
                            </Link>
                        ) : ebookStatus === "pending" ? (
                            <div className="flex items-center gap-1.5 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2 text-yellow-700 text-xs font-semibold mt-1.5 md:mt-0">
                                <Clock className="w-3.5 h-3.5 shrink-0" />
                                যাচাই হচ্ছে...
                            </div>
                        ) : (
                            <button
                                onClick={() => {
                                    if (!user) {
                                        nav("/login");
                                        return;
                                    }
                                    setShowPayment(true);
                                }}
                                className="btn-primary text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 mt-1.5 md:mt-0"
                            >
                                <CreditCard className="w-3.5 h-3.5" />
                                {ebookStatus === "expired"
                                    ? t("resubscribe")
                                    : t("subscribe_now")}
                            </button>
                        )
                    ) : book.stock <= 0 ? (
                        <div className="bg-brand-light/40 text-brand-navy/50 text-xs font-semibold px-3 py-2 rounded-lg mt-1.5 md:mt-0">
                            {t("out_of_stock")}
                        </div>
                    ) : alreadyInCart ? (
                        <button
                            onClick={() => nav("/cart")}
                            className="btn-primary text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 mt-1.5 md:mt-0"
                        >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            {t("view_cart")}
                        </button>
                    ) : (
                        <button
                            onClick={() => add(book)}
                            className="btn-primary text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 mt-1.5 md:mt-0"
                            data-testid={`add-to-cart-${ book.id }`}
                        >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            {t("add_to_cart")}
                        </button>
                    )}

                </div>
            </div>

            <EbookPaymentModal
                book={book}
                open={showPayment}
                onClose={() => setShowPayment(false)}
            />
        </div>
    );
}